<script setup lang="ts">
import { moduleName } from '@/enums/moduleNames';
import { getCachedGuestViewInsightMonthly, getCachedGuestViewInsightSevenDay, getCachedGuestViewInsightYearly, getCachedGuestViewResearchMonthly, getCachedGuestViewResearchSevenDay, getCachedGuestViewResearchYearly, getCachedUserViewInsightMonthly, getCachedUserViewInsightSevenDay, getCachedUserViewInsightYearly, getCachedUserViewResearchMonthly, getCachedUserViewResearchSevenDay, getCachedUserViewResearchYearly, getCachedUserViewInstitutionMonthly, getCachedUserViewInstitutionSevenDay, getCachedUserViewInstitutionYearly, getCachedGuestViewInstitutionSevenDay, getCachedGuestViewInstitutionMonthly, getCachedGuestViewInstitutionYearly, getCachedUserViewCircleSevenDay, getCachedUserViewCircleMonthly, getCachedUserViewCircleYearly, getCachedGuestViewCircleSevenDay, getCachedGuestViewCircleMonthly, getCachedGuestViewCircleYearly } from '@/service/analytics/basicAnalytics';
import { userPermissionStore } from '@/stores/user/permission';
import type { AxiosResponse } from 'axios';
import moment from 'moment';
import { computed, onMounted, reactive, ref, type Ref } from 'vue';
import BarChart from '../charts/barChart.vue';
import linearLoadingCard from '../loading/linearLoadingCard.vue';
import { plugin } from 'postcss';
import { Legend } from 'chart.js';

interface totalViewRecord {
  total: number, 
  target_date: Date,
  action: string
}

enum period {
  SEVENDAY = 'SEVENDAY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

const periodCounter = ref<number>(0)

const periodState = ref<period>(period.SEVENDAY)

interface dataset {
  label: string,
  data: number[],
  backgroundColor: string
}

var sortableCount = 0

const chartData = reactive({
  labels: [] as string[],
  researchData: {label: 'Research', data: [], backgroundColor: '#8AB8F5', maxBarThickness: 50} as dataset,
  insightData: {label: 'Insight', data: [], backgroundColor: '#4198D3', maxBarThickness: 50} as dataset,
  institutionData: {label: 'Institution', data: [], backgroundColor: '#474BB3', maxBarThickness: 50} as dataset,
  circleData: {label: 'Circle', data: [], backgroundColor: '#39548F', maxBarThickness: 50,display: true} as dataset,
})

const userPermission = userPermissionStore()

const totalViewNonUserResearch = ref<totalViewRecord[]>([])
const totalViewUserResearch = ref<totalViewRecord[]>([])
const totalViewUserInsight = ref<totalViewRecord[]>([])
const totalViewNonUserInsight = ref<totalViewRecord[]>([])
const totalViewUserInstitution = ref<totalViewRecord[]>([])
const totalViewNonUserInstitution = ref<totalViewRecord[]>([])
const totalViewUserCircle = ref<totalViewRecord[]>([])
const totalViewNonUserCircle = ref<totalViewRecord[]>([])

const fetchState = reactive({
  fetchingTotalViewSignedInUserForResearchInSevenDaysBack: true,
  fetchingTotalViewNonUserForResearchInSevenDaysBack: true,
  fetchingTotalViewSignedInUserForInsightInSevenDaysBack: true,
  fetchingTotalViewNonUserForInsightInSevenDaysBack: true,
  fetchingTotalViewSignedInUserForInstitutionInSevenDaysBack: true,
  fetchingTotalViewNonUserForInstitutionInSevenDaysBack: true,
  fetchingTotalViewSignedInUserForCircleInSevenDaysBack: true,
  fetchingTotalViewNonUserForCircleInSevenDaysBack: true,
  dataSorted: false,
})

const totalPermission = computed(()=>{
  var total = 0
  if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_NEWS_ANALYTIC)) {
    total = total + 1
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_RESEARCH_ANALYTIC)) {
    total = total + 1
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.INSTITUTION_ANALYTIC)) {
    total = total + 1
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.CIRCLE_ANALYTIC)) {
    total = total + 1
  }
  return total
})

const dataSet = computed(()=>{
  var dataList: dataset[] = []
  if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_NEWS_ANALYTIC)) {
    dataList.push(chartData.insightData)
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_RESEARCH_ANALYTIC)) {
    dataList.push(chartData.researchData)
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.INSTITUTION_ANALYTIC)) {
    dataList.push(chartData.institutionData)
  }
  if (userPermission.getUserPermission.read_action.includes(moduleName.CIRCLE_ANALYTIC)) {
    dataList.push(chartData.circleData)
  }
  return dataList
})

sortableCount = totalPermission.value * 2

console.log("count: ",sortableCount)


function sortLabelDate (arr: { total: number; target_date: Date; action: string}[], reset: boolean = false) {
  if (reset ) {
    chartData.labels = []
  }
  for (var x = 0; x < arr.length; x++) {
    var assigned = false
    const targetedDate = arr[x].target_date.toString()
    if (chartData.labels.length === 0) {
      chartData.labels.unshift(targetedDate)
      assigned = true
      continue
    }
    if (chartData.labels.includes(targetedDate)) {
      assigned = true
      continue
    }
    if (!assigned) {
      chartData.labels.push(arr[x].target_date.toString())
    }
  }
  sortableCount = sortableCount - 1
  if (sortableCount === 0) {
    chartData.labels.sort((a, b) => moment(a).diff(moment(b), 'days'))
    if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_RESEARCH_ANALYTIC)) {
      chartData.researchData.data = Array(chartData.labels.length).fill(0)
      assignedData(totalViewUserResearch, 'research')
      assignedData(totalViewNonUserResearch, 'research')
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_NEWS_ANALYTIC)) {
      chartData.insightData.data = Array(chartData.labels.length).fill(0)
      assignedData(totalViewUserInsight, 'insight')
      assignedData(totalViewNonUserInsight, 'insight')
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.INSTITUTION_ANALYTIC)) {
      chartData.institutionData.data = Array(chartData.labels.length).fill(0)
      assignedData(totalViewUserInstitution, 'institution')
      assignedData(totalViewNonUserInstitution, 'institution')
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.CIRCLE_ANALYTIC)) {
      chartData.circleData.data = Array(chartData.labels.length).fill(0)
      assignedData(totalViewUserCircle, 'circle')
      assignedData(totalViewNonUserCircle, 'circle')
    }
    fetchState.dataSorted = true
  }
}

function assignedData (refData: Ref, tag: string = '') {
  for (var x = 0; x < chartData.labels.length; x++) {
    var assigned = false
    for (var y = 0; y < refData.value.length; y++) {
      if (refData.value[y].target_date.toString() === chartData.labels[x]) {
        if (tag === 'research') chartData.researchData.data[x] = chartData.researchData.data[x] + refData.value[y].total
        if (tag === 'insight') chartData.insightData.data[x] = chartData.insightData.data[x] + refData.value[y].total
        if (tag === 'institution') chartData.institutionData.data[x] = chartData.institutionData.data[x] + refData.value[y].total
        if (tag === 'circle') chartData.circleData.data[x] = chartData.circleData.data[x] + refData.value[y].total
        assigned = true
        break;
      }
      else if (moment(refData.value[y].target_date).isAfter(moment(chartData.labels[x]))) {
        refData.value.splice(y-1, 0, {total: 0, target_date: moment(chartData.labels[x]).toDate(), action: 'ENTER'})
        assigned =  true
        break;
      }
    }
    if (!assigned) {
      refData.value.push({total: 0, target_date: moment(chartData.labels[x]).toDate(), action: 'ENTER'})
    }
  }
}

const compiledLabel = computed(()=> chartData.labels.map((item:string) => moment(item).format((periodState.value === period.MONTHLY) ? 'MMM YYYY' : ((periodState.value === period.YEARLY) ? 'YYYY' : 'DD MMM YYYY') )))

async function fetchTotalViewSignedInUserForResearch(targetPeriod: period) {
  fetchState.fetchingTotalViewSignedInUserForResearchInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedUserViewResearchSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedUserViewResearchMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedUserViewResearchYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewUserResearch.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  // totalPublishedNews.value = result.data.data.total
  fetchState.fetchingTotalViewSignedInUserForResearchInSevenDaysBack = false
}
async function fetchTotalViewNonUserForResearch(targetPeriod: period) {
  fetchState.fetchingTotalViewNonUserForResearchInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedGuestViewResearchSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedGuestViewResearchMonthly())
  }else if (targetPeriod === period.YEARLY) {
    result = (await getCachedGuestViewResearchYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewNonUserResearch.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  fetchState.fetchingTotalViewNonUserForResearchInSevenDaysBack = false
}
async function fetchTotalViewSignedInUserForinsight(targetPeriod: period) {
  fetchState.fetchingTotalViewSignedInUserForInsightInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedUserViewInsightSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedUserViewInsightMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedUserViewInsightYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewUserInsight.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  // totalPublishedNews.value = result.data.data.total
  fetchState.fetchingTotalViewSignedInUserForInsightInSevenDaysBack = false
}
async function fetchTotalViewNonUserForinsight(targetPeriod: period) {
  fetchState.fetchingTotalViewNonUserForInsightInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedGuestViewInsightSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedGuestViewInsightMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedGuestViewInsightYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewNonUserInsight.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  fetchState.fetchingTotalViewNonUserForInsightInSevenDaysBack = false
}
async function fetchTotalViewSignedInUserForInstitution(targetPeriod: period) {
  fetchState.fetchingTotalViewSignedInUserForInstitutionInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedUserViewInstitutionSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedUserViewInstitutionMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedUserViewInstitutionYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewUserInstitution.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  // totalPublishedNews.value = result.data.data.total
  fetchState.fetchingTotalViewSignedInUserForInstitutionInSevenDaysBack = false
}
async function fetchTotalViewNonUserForInstitution(targetPeriod: period) {
  fetchState.fetchingTotalViewNonUserForInstitutionInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedGuestViewInstitutionSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedGuestViewInstitutionMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedGuestViewInstitutionYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewNonUserInstitution.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  fetchState.fetchingTotalViewNonUserForInstitutionInSevenDaysBack = false
}
async function fetchTotalViewSignedInUserForCircle(targetPeriod: period) {
  fetchState.fetchingTotalViewSignedInUserForCircleInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedUserViewCircleSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedUserViewCircleMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedUserViewCircleYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewUserCircle.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
  }
  // totalPublishedNews.value = result.data.data.total
  fetchState.fetchingTotalViewSignedInUserForCircleInSevenDaysBack = false
}
async function fetchTotalViewNonUserForCircle(targetPeriod: period) {
  fetchState.fetchingTotalViewNonUserForCircleInSevenDaysBack = true
  var result = {} as AxiosResponse
  if (targetPeriod === period.SEVENDAY) {
    result = (await getCachedGuestViewCircleSevenDay())
  } else if (targetPeriod === period.MONTHLY) {
    result = (await getCachedGuestViewCircleMonthly())
  } else if (targetPeriod === period.YEARLY) {
    result = (await getCachedGuestViewCircleYearly())
  }
  if (periodState.value === targetPeriod) {
    totalViewNonUserCircle.value = result.data.data
    periodCounter.value = periodCounter.value + 1
    sortLabelDate(result.data.data, periodCounter.value === 1)
    console.log(result.data.data)
  }
  fetchState.fetchingTotalViewNonUserForCircleInSevenDaysBack = false
}

async function refetchData(targetPeriod: period) {
  if (totalPermission.value > 0) {
    periodState.value = targetPeriod
    fetchState.dataSorted = false
    sortableCount = totalPermission.value * 2
    chartData.labels = []
    chartData.insightData.data = []
    chartData.researchData.data = []
    chartData.institutionData.data = []
    chartData.circleData.data = []
    periodCounter.value = 0
    if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_RESEARCH_ANALYTIC)) {
      fetchTotalViewNonUserForResearch(periodState.value)
      fetchTotalViewSignedInUserForResearch(periodState.value)
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.ALGO_NEWS_ANALYTIC)) {
      fetchTotalViewSignedInUserForinsight(periodState.value)
      fetchTotalViewNonUserForinsight(periodState.value)
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.INSTITUTION_ANALYTIC)) {
      fetchTotalViewSignedInUserForInstitution(periodState.value)
      fetchTotalViewNonUserForInstitution(periodState.value)
    }
    if (userPermission.getUserPermission.read_action.includes(moduleName.CIRCLE_ANALYTIC)) {
      fetchTotalViewNonUserForCircle(periodState.value)
      fetchTotalViewSignedInUserForCircle(periodState.value)
    }
    
  }
}

onMounted(async ()=> {
  await refetchData(periodState.value)
})
</script>
<template>
    <div class="flex flex-col bg-white border-[1px] rounded-[6px] p-[16px] gap-[16px]" v-show="totalPermission > 0" >
        <div class="flex fex-row items-center">
        <div class="text-[20px] font-bold flex-grow">Total View</div>
        <div class="w-[16px] h-[16px]">
        </div>
        </div>
        <div class="flex fex-row items-center">
        <button class="text-[14px] flex-grow text-center border-b pb-[1px]" :class="[periodState === period.SEVENDAY ? 'font-bold border-b-2 border-black' : 'border-1']" @click="refetchData(period.SEVENDAY)">Last 7 days</button>
        <button class="text-[14px] flex-grow text-center border-b pb-[1px]" :class="[periodState === period.MONTHLY ? 'font-bold border-b-2 border-black' : 'border-1']" @click="refetchData(period.MONTHLY)">Monthly</button>
        <button class="text-[14px] flex-grow text-center border-b pb-[1px]" :class="[periodState === period.YEARLY ? 'font-bold border-b-2 border-black' : 'border-1']" @click="refetchData(period.YEARLY)">Yearly</button>
        </div>
        <div class="w-full my-[8px]">
        <BarChart v-if="fetchState.dataSorted" id="chartViewResearchAndInsight" :labels="compiledLabel" :datasets="dataSet" />
        <div v-if="!fetchState.dataSorted" class="w-full h-[250px]">
          <linearLoadingCard />
      </div>
        </div>
    </div>
</template>